import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import type { TaskRecord } from "@nexus/contracts";
import {
  deletePersonaOpportunitySet,
  fetchPersonaOwnBrand,
  fetchPersonaOpportunitySetDetail,
  fetchPersonaOpportunitySets,
  createUserGeneratedPersonaOpportunitySet,
  generatePersonaOpportunitySet,
  regeneratePersonaOpportunitySet,
  renamePersonaOpportunitySet,
  retryPersonaOpportunitySet,
  type GeneratePersonaOpportunitySetRequest,
  type PersonaOpportunitySetDetail,
  type PersonaOpportunitySetListPage,
  type PersonaOwnBrand,
  type RegeneratePersonaOpportunitySetRequest,
  type RetryPersonaOpportunitySetRequest,
} from "@/features/persona-opportunities/api/persona-opportunity-sets.api";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { ApiError, getLocalUserFacingApiMessage } from "@/lib/api-client";
import i18n from "@/lib/i18n";
import { Sentry } from "@/lib/sentry";
import { marketplaceKeys } from "./marketplace-query-keys";
import { personaOpportunityHubKeys } from "./persona-opportunity-hub.queries";
import { revealGenerationTask } from "./opportunity-sets.queries";
import { taskProgressKeys } from "./task-progress.queries";
import { dismissTask } from "@/features/task-progress/api/task-progress.api";
import { applyPersonaOpportunitySetListSuppression } from "@/features/persona-opportunities/lib/persona-opportunity-set-suppression";

/**
 * Backstop while a set is GENERATING. The primary refresh is invalidating list +
 * detail when the generate-set task hits progress 1 (500ms task poll). This
 * interval covers a missed task record. Cheap indexed GET; persona GET routes
 * log at `warn` so this stays off hot-path logs.
 */
export const PERSONA_GENERATION_POLL_MS = 2_000;

// ─── Key Factory ────────────────────────────────────────────────────

export const personaOpportunitySetKeys = {
  all: ["persona-opportunity-sets"] as const,
  /** List is cached per tenant so switching the active tenant can't show another tenant's sets. */
  list: (tenantId: string) => ["persona-opportunity-sets", "list", tenantId] as const,
  details: () => ["persona-opportunity-sets", "detail"] as const,
  detail: (tenantId: string, setId: string) =>
    ["persona-opportunity-sets", "detail", tenantId, setId] as const,
  /** Own-brand slug for the create-dialog competitor exclusion (request tenant). */
  ownBrand: (tenantId: string) => ["persona-opportunity-sets", "own-brand", tenantId] as const,
} as const;

/** Matches the tenant segment used by the persona set cache keys. */
export function normalizedPersonaOpportunitySetTenantId(
  tenantId: string | null | undefined,
): string {
  return tenantId || "__none__";
}

// ─── Query Options ──────────────────────────────────────────────────

/**
 * Sets list for the active tenant. `canListSets` comes from
 * `usePersonaOpportunityPermissions` — a viewer without it would only get a 403.
 */
export function personaOpportunitySetsQueryOptions(
  tenantId: string | null | undefined,
  options: { enabled?: boolean } = {},
) {
  const enabled = options.enabled ?? true;
  return queryOptions({
    queryKey: personaOpportunitySetKeys.list(normalizedPersonaOpportunitySetTenantId(tenantId)),
    queryFn: (): Promise<PersonaOpportunitySetListPage> => fetchPersonaOpportunitySets(),
    enabled: !!tenantId && enabled,
    // Keep Stopped rows hidden while worker soft-delete races list invalidation/poll.
    select: applyPersonaOpportunitySetListSuppression,
    // Polling is a pure function of server state: keep refetching while any set is
    // GENERATING, stop otherwise. Errors keep the poll alive so a generating card
    // doesn't freeze on a single failed refetch. Uses raw cache (pre-select) so a
    // suppressed stopped row still drives polling until the server omits it.
    refetchInterval: (query) => {
      if (query.state.status === "error") return PERSONA_GENERATION_POLL_MS;
      const page = query.state.data as PersonaOpportunitySetListPage | undefined;
      return page?.items.some((set) => set.status === "GENERATING")
        ? PERSONA_GENERATION_POLL_MS
        : false;
    },
  });
}

const SETS_PAGE_SIZE = 50;

/** Further set pages for the hub. The route still owns the first page and optimistic rows. */
export function personaOpportunitySetsInfiniteQueryOptions(
  tenantId: string | null | undefined,
  options: { enabled?: boolean } = {},
) {
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  return infiniteQueryOptions({
    queryKey: [...personaOpportunitySetKeys.list(normalizedTenant), "pages"] as const,
    queryFn: ({ pageParam }) =>
      fetchPersonaOpportunitySets({ cursor: pageParam, limit: SETS_PAGE_SIZE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: Boolean(tenantId) && (options.enabled ?? true),
  });
}

/** Full set detail for the workspace. `setId` is empty while the sets grid is showing. */
export function personaOpportunitySetDetailQueryOptions(
  tenantId: string | null | undefined,
  setId: string | null | undefined,
  options: { enabled?: boolean; pollMs?: number | false } = {},
) {
  const enabled = options.enabled ?? true;
  const id = setId ?? "";
  return queryOptions({
    queryKey: personaOpportunitySetKeys.detail(
      normalizedPersonaOpportunitySetTenantId(tenantId),
      id || "__none__",
    ),
    queryFn: (): Promise<PersonaOpportunitySetDetail> => fetchPersonaOpportunitySetDetail(id),
    enabled: !!tenantId && !!id && enabled,
    // Poll while the set itself is GENERATING, or while the caller asks (e.g. scripts
    // generation) so opportunity SCRIPTS_GENERATED status lands for the filter.
    refetchInterval: (query) => {
      if (options.pollMs) return options.pollMs;
      const detail = query.state.data as PersonaOpportunitySetDetail | undefined;
      return detail?.set.status === "GENERATING" ? PERSONA_GENERATION_POLL_MS : false;
    },
    meta: { skipGlobalError: true },
  });
}

/** Both caches for a tenant — the list drives the grid, the detail drives the workspace. */
export function invalidatePersonaOpportunitySets(
  queryClient: QueryClient,
  tenantId: string | null | undefined,
): void {
  const tid = normalizedPersonaOpportunitySetTenantId(tenantId);
  void queryClient.invalidateQueries({ queryKey: personaOpportunitySetKeys.list(tid) });
  void queryClient.invalidateQueries({ queryKey: personaOpportunitySetKeys.details() });
}

/**
 * Resolved own-brand competitor slug for the active tenant. Used only by the
 * persona create dialog to exclude the client from competitor options.
 */
export function personaOwnBrandQueryOptions(
  tenantId: string | null | undefined,
  options: { enabled?: boolean } = {},
) {
  const enabled = options.enabled ?? true;
  return queryOptions({
    queryKey: personaOpportunitySetKeys.ownBrand(
      normalizedPersonaOpportunitySetTenantId(tenantId),
    ),
    queryFn: (): Promise<PersonaOwnBrand> => fetchPersonaOwnBrand(),
    enabled: !!tenantId && enabled,
    staleTime: 1000 * 60 * 60,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────

/**
 * These mutations end at the 202, exactly like legacy opportunity sets end at
 * their 201. The generation's outcome is not the mutation's business: the polled
 * list carries set status and the task-progress record carries the set id and the
 * end of the run.
 *
 * Polling `/status/:correlationId` from here instead would mean a promise living
 * up to ~15 minutes that the page cannot cancel, whose late result lands on
 * whatever generation happens to be current when it settles.
 */
function reportGenerationRequestFailure(err: unknown): void {
  Sentry.captureException(err);
  // A 409 means this set is already generating — a stale card the user acted on
  // before the list caught up. It is the one rejection whose reason the user can
  // act on, and `getUserFacingApiMessage` flattens everything to a generic string.
  if (err instanceof ApiError && err.status === 409) {
    toast.error(
      i18n.t(
        "personaOpportunities.toast.setAlreadyGenerating",
        "This set is already generating.",
      ),
    );
    return;
  }
  const message = getLocalUserFacingApiMessage(err);
  if (message) toast.error(message);
}

/**
 * The 202's `correlationId` reaches the caller through the mutation result — pass
 * a per-call `onSuccess` to `mutate`. A shared hook-level callback could not tell
 * concurrent runs apart.
 */
export function useCreateUserGeneratedPersonaOpportunitySet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => createUserGeneratedPersonaOpportunitySet(name),
    onSuccess: () => {
      const activeTenantId = useAuth.getState().activeTenantId;
      invalidatePersonaOpportunitySets(queryClient, activeTenantId);
      void queryClient.invalidateQueries({ queryKey: personaOpportunityHubKeys.all });
    },
    meta: { skipGlobalError: true },
  });
}

export function useGeneratePersonaOpportunitySet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: GeneratePersonaOpportunitySetRequest) =>
      generatePersonaOpportunitySet(body),
    onSuccess: () => {
      // The BFF registers the task record before responding, so the sidebar can
      // open on the 202 rather than waiting for the first discovery poll.
      revealGenerationTask(queryClient);
    },
    onError: reportGenerationRequestFailure,
    meta: { skipGlobalError: true },
  });
}

export function useDeletePersonaOpportunitySet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePersonaOpportunitySet(id),
    onSuccess: (_data, id) => {
      const activeTenantId = useAuth.getState().activeTenantId;
      invalidatePersonaOpportunitySets(queryClient, activeTenantId);
      void queryClient.invalidateQueries({ queryKey: personaOpportunityHubKeys.all });
      if (activeTenantId) {
        void queryClient.invalidateQueries({ queryKey: marketplaceKeys.orders(activeTenantId) });
        queryClient.removeQueries({
          queryKey: personaOpportunitySetKeys.detail(
            normalizedPersonaOpportunitySetTenantId(activeTenantId),
            id,
          ),
        });
      }
    },
  });
}

export function useRenamePersonaOpportunitySet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      renamePersonaOpportunitySet(id, name),
    onSuccess: () => {
      const activeTenantId = useAuth.getState().activeTenantId;
      invalidatePersonaOpportunitySets(queryClient, activeTenantId);
    },
  });
}

export interface RetryPersonaOpportunitySetVariables {
  setId: string;
  body: RetryPersonaOpportunitySetRequest;
}

export function useRetryPersonaOpportunitySet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ setId, body }: RetryPersonaOpportunitySetVariables) =>
      retryPersonaOpportunitySet(setId, body),
    onSuccess: () => {
      revealGenerationTask(queryClient);
    },
    onError: reportGenerationRequestFailure,
    meta: { skipGlobalError: true },
  });
}

/**
 * Retry a failed persona opportunity-set generation from the task-progress sidebar.
 * Always POSTs `/persona-opportunity-sets/:id/retry` — never leftover `/opportunity-sets/:id/retry`.
 */
export function useRetryPersonaOpportunitySetTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (task: TaskRecord) => {
      const setId = task.navigation?.params?.setId;
      if (!setId) throw new Error("Task has no associated set ID");
      if (task.tenantId && useAuth.getState().activeTenantId !== task.tenantId) {
        useAuth.getState().setActiveTenant(task.tenantId);
      }
      return retryPersonaOpportunitySet(setId, {
        tenantId: task.tenantId,
        idempotencyKey: crypto.randomUUID(),
      });
    },
    onSuccess: (_updated, task) => {
      void dismissTask(task.correlationId).catch(() => {});
      const email = useAuth.getState().user?.email;
      if (email) {
        queryClient.setQueryData<TaskRecord[]>(
          taskProgressKeys.active(email),
          (prev) => prev?.filter((t) => t.correlationId !== task.correlationId),
        );
      }
      revealGenerationTask(queryClient);
      const activeTenantId = useAuth.getState().activeTenantId;
      invalidatePersonaOpportunitySets(queryClient, activeTenantId);
    },
    onError: (error, task) => {
      const isNotFound = error instanceof ApiError && error.status === 404;
      if (isNotFound) {
        void dismissTask(task.correlationId).catch(() => {});
        const email = useAuth.getState().user?.email;
        if (email) {
          queryClient.setQueryData<TaskRecord[]>(
            taskProgressKeys.active(email),
            (prev) => prev?.filter((t) => t.correlationId !== task.correlationId),
          );
        }
      }
    },
  });
}

export interface RegeneratePersonaOpportunitySetVariables {
  setId: string;
  body: RegeneratePersonaOpportunitySetRequest;
}

export function useRegeneratePersonaOpportunitySet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ setId, body }: RegeneratePersonaOpportunitySetVariables) =>
      regeneratePersonaOpportunitySet(setId, body),
    onSuccess: () => {
      revealGenerationTask(queryClient);
    },
    onError: reportGenerationRequestFailure,
    meta: { skipGlobalError: true },
  });
}
